import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { toast } from "sonner";

const StoreContext = createContext(null);

export const StoreProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("laxmi_token"));
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("laxmi_cart") || "[]"); } catch { return []; }
  });
  const [wishlist, setWishlist] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("laxmi_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    if (token) {
      localStorage.setItem("laxmi_token", token);
      api.get("/auth/me").then((r) => {
        setUser(r.data);
        setWishlist(r.data.wishlist || []);
      }).catch(() => {
        localStorage.removeItem("laxmi_token");
        setToken(null);
      });
    } else {
      localStorage.removeItem("laxmi_token");
      setUser(null);
      setWishlist([]);
    }
  }, [token]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setToken(r.data.token);
    setUser(r.data.user);
    toast.success(`Welcome back, ${r.data.user.name}`);
    return r.data.user;
  };

  const register = async (name, email, password) => {
    const r = await api.post("/auth/register", { name, email, password });
    setToken(r.data.token);
    setUser(r.data.user);
    toast.success(`Welcome to Laxmi Aura, ${r.data.user.name}`);
    return r.data.user;
  };

  const logout = () => {
    setToken(null);
    toast("Signed out");
  };

  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const found = prev.find((i) => i.product_id === product.id);
      if (found) {
        return prev.map((i) => i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i);
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        slug: product.slug,
        price: product.price,
        image: product.images?.[0],
        quantity: qty,
      }];
    });
    toast.success(`${product.name} added to cart`);
    setCartOpen(true);
  }, []);

  const updateQty = (product_id, quantity) => {
    if (quantity <= 0) return removeFromCart(product_id);
    setCart((prev) => prev.map((i) => i.product_id === product_id ? { ...i, quantity } : i));
  };

  const removeFromCart = (product_id) => {
    setCart((prev) => prev.filter((i) => i.product_id !== product_id));
    toast("Removed from cart");
  };

  const clearCart = () => setCart([]);

  const toggleWishlist = async (productId) => {
    if (!token) {
      toast.error("Please sign in to save to wishlist");
      return false;
    }
    const r = await api.post(`/wishlist/${productId}`);
    setWishlist(r.data.wishlist);
    const added = r.data.wishlist.includes(productId);
    toast.success(added ? "Added to wishlist" : "Removed from wishlist");
    return added;
  };

  const cartSubtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  return (
    <StoreContext.Provider value={{
      user, token, login, register, logout,
      cart, addToCart, updateQty, removeFromCart, clearCart,
      cartSubtotal, cartCount, cartOpen, setCartOpen,
      wishlist, toggleWishlist,
    }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used inside StoreProvider");
  return ctx;
};
